/**
 * Expands recurring meetings into occurrence objects for calendar display.
 * Uses Zoom-shaped recurrence_rule: type 1=Daily, 2=Weekly, 3=Monthly.
 *
 * @param {Array} meetings - Raw meetings from dashboard (may include isRecurring + recurrenceRule)
 * @param {Date} rangeStart - Start of visible calendar range
 * @param {Date} rangeEnd - End of visible calendar range
 * @returns {Array} Flattened meetings: one-off as-is, recurring expanded to occurrences in range
 */

import {
  parseISO,
  addDays,
  addMonths,
  startOfDay,
  isBefore,
  isAfter,
  getDay,
} from 'date-fns'

// Zoom weekly_days: 1=Sun, 2=Mon, ..., 7=Sat. date-fns getDay: 0=Sun, 1=Mon, ..., 6=Sat.
const zoomToJsDay = (n) => (n >= 1 && n <= 7 ? n - 1 : -1)

/**
 * Get occurrence dates for a recurring meeting in the given range.
 * Zoom: type 1=Daily, 2=Weekly, 3=Monthly. weekly_days: "1,2,3" (1=Sun..7=Sat).
 */
function getOccurrenceDates(firstStart, rule, rangeStart, rangeEnd) {
  const type = rule?.type ?? 2
  const interval = Math.max(1, rule?.repeat_interval ?? 1)
  const weeklyDaysStr = String(rule?.weekly_days ?? '')
  const endTimes = rule?.end_times ?? 0
  const endDateTime = rule?.end_date_time ? parseISO(rule.end_date_time) : null
  const neverEnds = rule?.never_ends === true

  const first = parseISO(firstStart)
  const firstDay = getDay(first)
  const rangeStartDay = startOfDay(rangeStart)
  const rangeEndDay = startOfDay(rangeEnd)
  const maxEnd = neverEnds ? addMonths(rangeEndDay, 3) : endDateTime || addMonths(rangeEndDay, 3)

  const validWeeklyDays = weeklyDaysStr
    .split(',')
    .map((d) => zoomToJsDay(parseInt(String(d).trim(), 10)))
    .filter((d) => d >= 0)
    .sort((a, b) => a - b)

  const dates = []
  let count = 0

  const shouldStop = () => {
    if (endTimes > 0 && count >= endTimes) return true
    if (endDateTime && isAfter(dates[dates.length - 1] || first, endDateTime)) return true
    return false
  }

  const addIfInRange = (d) => {
    const day = startOfDay(d)
    if (isBefore(day, rangeStartDay) || isAfter(day, rangeEndDay)) return
    if (endDateTime && isAfter(day, endDateTime)) return
    dates.push(new Date(d))
    count++
  }

  if (type === 1) {
    // Daily: every N days from first
    let cursor = new Date(first)
    while (!isAfter(cursor, maxEnd) && !(endTimes > 0 && count >= endTimes)) {
      addIfInRange(cursor)
      cursor = addDays(cursor, interval)
    }
  } else if (type === 2) {
    // Weekly: every N weeks on given weekdays. First occurrence anchors the series.
    if (validWeeklyDays.length === 0) {
      addIfInRange(first)
      return dates
    }
    const dayMs = 24 * 60 * 60 * 1000
    const weekMs = 7 * dayMs
    const firstTime = first.getHours() * 3600000 + first.getMinutes() * 60000 + first.getSeconds() * 1000
    for (let w = 0; ; w += interval) {
      if (endTimes > 0 && count >= endTimes) break
      const baseWeekStart = startOfDay(first).getTime() - firstDay * dayMs + w * weekMs
      for (const dayOffset of validWeeklyDays) {
        const occDate = new Date(baseWeekStart + dayOffset * dayMs + firstTime)
        if (isBefore(occDate, first)) continue
        if (isAfter(occDate, maxEnd)) break
        addIfInRange(occDate)
        if (endTimes > 0 && count >= endTimes) break
      }
      const nextCycleStart = new Date(baseWeekStart + interval * weekMs)
      if (isAfter(nextCycleStart, maxEnd)) break
    }
  } else if (type === 3) {
    // Monthly: every N months, same date
    let cursor = new Date(first)
    while (!isAfter(cursor, maxEnd) && !(endTimes > 0 && count >= endTimes)) {
      addIfInRange(cursor)
      cursor = addMonths(cursor, interval)
    }
  }

  return dates
}

export function expandRecurringMeetings(meetings, rangeStart, rangeEnd) {
  const result = []

  // Children = actual occurrences (meetings with a parentMeetingId).
  // Only ended children are real past occurrences; future children (status='created') should
  // not be shown directly — the parent expansion covers them as virtual slots.
  const allChildren = meetings.filter((m) => m.parentMeetingId)
  const endedChildren = allChildren.filter((m) => m.status === 'ended')
  const parents = meetings.filter((m) => !m.parentMeetingId)

  // Build set of (parentMeetingId, dateKey) for actual ended occurrences so parent
  // expansion skips those dates (they already have a real child row).
  const actualOccurrenceKeys = new Set()
  for (const c of endedChildren) {
    const start = c.startTime ?? c.scheduledStartTime
    if (start) {
      const dateKey = startOfDay(parseISO(start)).toISOString()
      actualOccurrenceKeys.add(`${c.parentMeetingId}::${dateKey}`)
    }
  }

  // Add only ended children (actual past occurrences)
  for (const c of endedChildren) {
    const start = c.startTime ?? c.scheduledStartTime
    if (start) result.push(c)
  }

  // Expand parents, but skip dates that have an actual child
  for (const m of parents) {
    const start = m.startTime ?? m.scheduledStartTime
    if (!start) {
      // Meetings without a scheduled time still appear (e.g. "start now" meetings)
      result.push(m)
      continue
    }

    const isRecurring = m.isRecurring === true
    const rule = m.recurrenceRule && typeof m.recurrenceRule === 'object' ? m.recurrenceRule : null

    if (!isRecurring || !rule?.type) {
      result.push(m)
      continue
    }

    const occurrenceDates = getOccurrenceDates(start, rule, rangeStart, rangeEnd)
    for (const occDate of occurrenceDates) {
      const dateKey = startOfDay(occDate).toISOString()
      if (actualOccurrenceKeys.has(`${m.meetingId}::${dateKey}`)) continue

      const baseStart = parseISO(start)
      const occStart = new Date(
        occDate.getFullYear(),
        occDate.getMonth(),
        occDate.getDate(),
        baseStart.getHours(),
        baseStart.getMinutes(),
        baseStart.getSeconds()
      )
      const durationMs =
        (m.endTime ? new Date(m.endTime).getTime() : occStart.getTime() + 60 * 60 * 1000) -
        baseStart.getTime()
      const occEnd = new Date(occStart.getTime() + durationMs)

      result.push({
        ...m,
        startTime: occStart.toISOString(),
        scheduledStartTime: occStart.toISOString(),
        endTime: occEnd.toISOString(),
      })
    }
  }
  return result
}
